import {classes} from '@bitstillery/common/lib/utils'
import tippy from 'tippy.js'
import {MithrilComponent} from '@bitstillery/mithril'
import {random_string} from '@bitstillery/common/lib/utils'
import m from '@bitstillery/mithril'
import type {Content, Instance, Placement, Props} from 'tippy.js'
import {TipMemo} from './tip_memo'
import type {Argument} from 'classnames'

export interface TippyAttrs {
    allowHTML?: boolean
    animation?: string | boolean
    appendTo?: Element | ((ref: Element) => Element)
    arrow?: boolean | string | SVGElement | DocumentFragment
    className?: Argument
    /** Absent where a wrapping component (Progress) has no tip to show; tippy then renders an empty one. */
    content?: Content | undefined
    delay?: number | [number | null, number | null]
    interactive?: boolean
    inertia?: boolean
    maxWidth?: number | string
    placement?: Placement | undefined
    role?: string
    theme?: string
    zIndex?: number

    onclick?: (event: MouseEvent) => unknown
}

export interface TippyJsxAttrs extends Omit<TippyAttrs, 'content'> {
    content: m.Children
    duration?: number | [number, number]
    trigger?: 'mouseenter' | 'click' | 'focus' | 'focusin' | 'manual'
}

/**
 * Wraps an element into a Tippy tooltip with allowHTML true.
 *
 * Usage:
 *   <TippyWrapper content={`<div>Number of cases in stock</div>`}>
 *      ...
 *   </TippyWrapper>
 *
 * See https://atomiks.github.io/tippyjs/ and https://atomiks.github.io/tippyjs/v6/all-props/.
 */
export class Tippy extends MithrilComponent<TippyAttrs> {
    id = random_string(8, false)
    tippy_instance?: Instance

    /** Skips tippy when the content has not changed; see TipMemo. */
    private tip_memo = new TipMemo<Content | undefined>()

    override oncreate(vn: m.Vnode<TippyAttrs>): void {
        const element = document.querySelector(`#${this.id}`)
        if (element) {
            this.tip_memo.prime(vn.attrs.content, vn.attrs.content)
            this.tippy_instance = tippy(element, {
                allowHTML: vn.attrs.allowHTML ? vn.attrs.allowHTML : true,
                ...(vn.attrs.appendTo === undefined ? {} : {appendTo: vn.attrs.appendTo}),
                ...content_props(vn.attrs.content),
                delay: vn.attrs.delay ? vn.attrs.delay : 0,
                interactive: vn.attrs.interactive ? vn.attrs.interactive : false,
                ...(vn.attrs.placement === undefined ? {} : {placement: vn.attrs.placement}),
                zIndex: vn.attrs.zIndex ? vn.attrs.zIndex : 100_000,
            })
        }
    }

    override onupdate(vn: m.Vnode<TippyAttrs>): void {
        if (!this.tippy_instance) {
            return
        }
        // `setContent` goes through tippy's `setProps`, which re-evaluates its whole prop set and writes
        // attributes on the reference element — too expensive to do on every redraw for content that has
        // not changed. Here the content IS the source, so an unchanged value costs one comparison.
        const next = this.tip_memo.next(vn.attrs.content, () => vn.attrs.content)
        if (next) {
            this.tippy_instance.setContent(next.content ?? '')
        }
    }

    override onremove() {
        if (this.tippy_instance) {
            this.tippy_instance.destroy()
        }
    }

    view(vnode: m.Vnode<TippyAttrs>): m.Children {
        return (
            <span className={classes('c-tippy', vnode.attrs.className)} id={this.id} onclick={vnode.attrs.onclick}>
                {vnode.children}
            </span>
        )
    }
}

/**
 * A variant of Tippy that accepts JSX content directly.
 *
 * Usage:
 *   <TippyJsx content={<div><strong>Bold content</strong><div>More content</div></div>}>
 *      ...
 *   </TippyJsx>
 *
 * The JSX content will be rendered to HTML and passed to tippy.js.
 */
export class TippyJsx extends MithrilComponent<TippyJsxAttrs> {
    tippy_instance?: Instance
    /** Skips tippy when the converted HTML has not changed; see TipMemo. */
    private tip_memo = new TipMemo<string>()

    private convert_jsx_to_html(content: m.Children): string {
        if (!content) {
            return ''
        }
        if (Array.isArray(content)) {
            const temp_div = document.createElement('div')
            m.render(temp_div, content)
            return temp_div.innerHTML
        }
        if (typeof content === 'object' && 'tag' in content) {
            const temp_div = document.createElement('div')
            m.render(temp_div, content)
            return temp_div.innerHTML
        }
        return String(content)
    }

    override onupdate(vn: m.Vnode<TippyJsxAttrs>): void {
        if (!this.tippy_instance) {
            return
        }
        // JSX content is a fresh vnode object every redraw, so its identity can never match and the
        // conversion still runs — but comparing the resulting HTML keeps tippy itself out of the redraw
        // path, which is where the cost was.
        const next = this.tip_memo.next(vn.attrs.content, () => this.convert_jsx_to_html(vn.attrs.content))
        if (next) {
            this.tippy_instance.setContent(next.content)
        }
    }

    override onremove(): void {
        if (this.tippy_instance) {
            this.tippy_instance.destroy()
        }
    }

    view(vnode: m.Vnode<TippyJsxAttrs>): m.Children {
        return (
            <span
                className={classes(vnode.attrs.className)}
                onclick={vnode.attrs.onclick}
                oncreate={(span_vnode: m.VnodeDOM) => {
                    const initial_html = this.convert_jsx_to_html(vnode.attrs.content)
                    this.tip_memo.prime(vnode.attrs.content, initial_html)
                    this.tippy_instance = tippy(span_vnode.dom, {
                        allowHTML: vnode.attrs.allowHTML !== false,
                        ...(vnode.attrs.appendTo === undefined ? {} : {appendTo: vnode.attrs.appendTo}),
                        content: initial_html,
                        delay: vnode.attrs.delay ? vnode.attrs.delay : ([500, 0] as [number, number]),
                        ...(vnode.attrs.duration ? {duration: vnode.attrs.duration} : {}),
                        interactive: vnode.attrs.interactive !== false,
                        interactiveBorder: 10,
                        ...(vnode.attrs.placement === undefined ? {} : {placement: vnode.attrs.placement}),
                        trigger: vnode.attrs.trigger ? vnode.attrs.trigger : 'mouseenter',
                        zIndex: vnode.attrs.zIndex ? vnode.attrs.zIndex : 100_000,
                    })
                }}
            >
                {vnode.children}
            </span>
        )
    }
}

export const tip = tippy

/** Tippy skips an undefined prop and keeps the one it had, so leaving the key out is the same call. */
export function content_props(content: Content | undefined): Partial<Props> {
    return content === undefined ? {} : {content}
}
